<template>
    <div>
        <div class="card shadow mb-5">
            <div class="card-header">Synthèse annuelle client</div>

            <b-card-body>
                <div v-if="!hasErrors" class="row mb-3">
                    <div class="col-4">
                        <search-client
                            size="sm"
                            :show-label="false"
                            search-url="/clients/search-with-prospects"
                            show-feedback="false"
                            @client-is-set="setClientId"
                            @searching-client="resetClient"
                        />
                    </div>
                    <div class="col-3 offset-1">
                        <b-button-group
                            v-if="clientIsSet"
                            size="sm"
                            class="btn-block"
                        >
                            <b-button
                                :pressed="reportUnit === 'hours'"
                                variant="outline-primary"
                                @click="setReportUnit('hours')"
                            >
                                En heures
                            </b-button>
                            <b-button
                                :pressed="reportUnit === 'euros'"
                                variant="outline-primary"
                                @click="setReportUnit('euros')"
                            >
                                En euros
                            </b-button>
                        </b-button-group>
                    </div>
                    <div class="col-4 text-right">
                        <year-handler
                            v-if="clientIsSet"
                            :disabled="loading"
                            :start="meta.start"
                            :end="meta.end"
                            :next="meta.next"
                            :prev="meta.prev"
                            @year-has-changed="updateData"
                        />
                    </div>
                </div>
                <div v-if="loading">
                    <loading v-if="clientIsSet" />
                </div>

                <div v-else>
                    <b-alert variant="danger" :show="hasErrors">
                        <span v-html="errorMessage"></span>
                    </b-alert>

                    <b-table
                        v-if="clientIsSet"
                        responsive
                        small
                        :items="items"
                        :fields="fields"
                        :filter="filter"
                        :per-page="perPage"
                        :current-page="currentPage"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                        primary-key="id"
                        @filtered="onFiltered"
                    >
                        <template #cell(m1)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.m1"
                            />
                        </template>
                        <template #cell(m2)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.m2"
                            />
                        </template>
                        <template #cell(m3)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.m3"
                            />
                        </template>
                        <template #cell(m4)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.m4"
                            />
                        </template>
                        <template #cell(m5)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.m5"
                            />
                        </template>
                        <template #cell(m6)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.m6"
                            />
                        </template>
                        <template #cell(m7)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.m7"
                            />
                        </template>
                        <template #cell(m8)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.m8"
                            />
                        </template>
                        <template #cell(m9)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.m9"
                            />
                        </template>
                        <template #cell(m10)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.m10"
                            />
                        </template>
                        <template #cell(m11)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.m11"
                            />
                        </template>
                        <template #cell(m12)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.m12"
                            />
                        </template>
                        <template #cell(sum)="data">
                            <month-charge-handler
                                :report-unit="reportUnit"
                                :charge="data.item.sum"
                            />
                        </template>
                        <template #cell(week_limit)="data">
                            <b-progress
                                v-if="reportUnit === 'hours'"
                                :value="data.item.sum.duration"
                                :max="meta.year_limit.duration"
                                variant="success"
                                height="12px"
                                class="mt-1"
                            />
                            <b-progress
                                v-else
                                :value="data.item.sum.amount"
                                :max="meta.year_limit.amount"
                                variant="success"
                                height="12px"
                                class="mt-1"
                            />
                        </template>

                        <template slot="bottom-row">
                            <td class="td-bold">TOTAL</td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalM1"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalM2"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalM3"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalM4"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalM5"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalM6"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalM7"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalM8"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalM9"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalM10"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalM11"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalM12"
                                />
                            </td>
                            <td class="text-right td-bold">
                                <month-charge-handler
                                    :report-unit="reportUnit"
                                    :charge="totalSum"
                                />
                            </td>
                            <td>
                                <b-progress
                                    v-if="reportUnit === 'hours'"
                                    :value="totalSum.duration"
                                    :max="meta.year_limit.duration"
                                    variant="secondary"
                                    height="12px"
                                    class="mt-1"
                                />
                                <b-progress
                                    v-else
                                    :value="totalSum.amount"
                                    :max="meta.year_limit.amount"
                                    variant="secondary"
                                    height="12px"
                                    class="mt-1"
                                />
                            </td>
                        </template>
                    </b-table>
                    <b-alert :show="meta.not_affected > 0" variant="warning">
                        ATTENTION : {{ meta.not_affected }}
                        <span v-if="meta.not_affected > 1">
                            heures supplémentaires ne sont pas affectées.
                        </span>
                        <span v-else>
                            heure supplémentaire n'est pas affectée.
                        </span>
                        Consultez le tableau croisé dynamique pour plus
                        d'information.
                    </b-alert>
                </div>
                <b-button
                    v-if="clientIsSet && !pivotDisplay"
                    size="sm"
                    variant="primary"
                    block
                    @click="showPivot"
                >
                    Afficher le tableau croisé dynamique
                </b-button>
                <b-button
                    v-if="clientIsSet && pivotDisplay"
                    size="sm"
                    variant="primary"
                    block
                    class="mb-3"
                    @click="hidePivot"
                >
                    Masquer le tableau croisé dynamique
                </b-button>
                <pivot-client-report
                    v-if="pivotDisplay"
                    :key="key"
                    :client-id="client_id"
                    :year="currentDate.substring(0, 4)"
                    :report-unit="reportUnit"
                />
            </b-card-body>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import TableHandler from '../../../common/mixins/TableHandler.vue'
import DashboardHandler from '../../../dashboard/mixins/DashboardHandler.vue'
import MonthChargeHandler from '../../../common/components/MonthChargeHandler.vue'
import loading from '../../../common/components/loading.vue'
import YearHandler from './YearHandler.vue'
import SearchClient from '../../clients/components/SearchClient.vue'
import PivotClientReport from './PivotClientReport.vue'

export default {
    name: 'ClientYearReport',

    components: {
        MonthChargeHandler,
        loading,
        YearHandler,
        SearchClient,
        PivotClientReport,
    },

    mixins: [TableHandler, DashboardHandler],

    props: {
        todayY: '',
        todayM: '',
        todayD: '',
    },

    data() {
        return {
            reportUnit: 'hours',
            lastRequestId: 0,
            clientIsSet: false,
            client_id: 0,
            key: 'pivot',
            pivotDisplay: false,
            meta: {},
            fields: [
                {
                    key: 'type',
                    label: 'Affectation',
                    sortable: true,
                },
                {
                    key: 'm1',
                    label: 'jan',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'm2',
                    label: 'fév',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'm3',
                    label: 'mar',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'm4',
                    label: 'avr',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'm5',
                    label: 'mai',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'm6',
                    label: 'jun',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'm7',
                    label: 'jul',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'm8',
                    label: 'aou',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'm9',
                    label: 'sep',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'm10',
                    label: 'oct',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'm11',
                    label: 'nov',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'm12',
                    label: 'déc',
                    sortable: true,
                    class: 'text-right',
                },
                {
                    key: 'sum',
                    label: 'TOTAL',
                    sortable: true,
                    class: 'text-right td-bold',
                },
                {
                    key: 'week_limit',
                    label: '',
                    class: 'col-uniform',
                },
            ],
        }
    },

    async created() {
        this.$store.commit(
            'timeSheetsVM/SET_CURRENT_DATE',
            `${this.todayY}/${this.todayM}/${this.todayD}`
        )
    },

    methods: {
        showPivot() {
            this.pivotDisplay = true
        },

        hidePivot() {
            this.pivotDisplay = false
        },

        async updateData() {
            this.hidePivot()
            await this.getItems()
        },

        async getItems() {
            // only the last request fills the table: an older response arriving later is ignored
            this.lastRequestId += 1
            const requestId = this.lastRequestId
            try {
                this.loading = true
                const response = await axios.get(
                    `/time-sheets/reporting/clients/${this.client_id}/year/${this.currentDate}`
                )
                if (requestId !== this.lastRequestId) {
                    return
                }
                // the report must be the one of the selected client
                if (Number(response.data.meta.client) !== Number(this.client_id)) {
                    this.handleErrors()
                    this.errorMessage =
                        'Les données reçues ne correspondent pas au client sélectionné. Veuillez réactualiser la page.'
                    return
                }
                this.items = response.data.data
                this.meta = response.data.meta
                this.loading = false
                this.updateTotalRows()
            } catch (error) {
                console.log(error)
                if (requestId !== this.lastRequestId) {
                    return
                }
                this.handleErrors(error.response)
            }
        },

        setClientId(client) {
            this.client_id = client.id
            this.clientIsSet = true
            this.updateKey()
            this.getItems()
            // this.$emit('client-is-set', client)
        },

        resetClient() {
            // this.$emit('client-is-not-set')
        },

        setReportUnit(units) {
            this.reportUnit = units
            this.updateKey()
        },

        updateKey() {
            this.key = Date.now()
        },
    },

    computed: {
        ...mapState({
            accountingFirmUser: (state) =>
                state.timeSheetsVM.filters.accountingFirmUser,
            currentDate: (state) => state.timeSheetsVM.currentDate,
        }),

        totalM1() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m1.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m1.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalM2() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m2.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m2.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalM3() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m3.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m3.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalM4() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m4.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m4.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalM5() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m5.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m5.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalM6() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m6.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m6.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalM7() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m7.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m7.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalM8() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m8.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m8.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalM9() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m9.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m9.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalM10() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m10.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m10.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalM11() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m11.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m11.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalM12() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m12.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.m12.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalSum() {
            const duration = this.items.reduce(
                (accum, item) => accum + parseFloat(item.sum.duration),
                0
            )
            const amount = this.items.reduce(
                (accum, item) => accum + parseFloat(item.sum.amount),
                0
            )
            return {
                duration,
                amount,
            }
        },

        totalChargeVariant() {
            const charge = (100 * this.totalSum) / this.meta.year_limit
            if (charge > this.meta.success) {
                return 'success'
            }
            if (charge > this.meta.warning) {
                return 'warning'
            }
            return 'danger'
        },
    },
}
</script>

<style scoped></style>
